import { format } from 'date-fns';
import { useEffect, useState } from 'react';

/**
 * When a rate-limited request may be retried, from the server's `Retry-After`,
 * and whether that moment has come.
 *
 * `failedAt` anchors the wait to the refusal rather than to whenever the caller
 * mounts, so a notice shown again for an error minutes old knows the wait is
 * already over. It is also what resets the wait: a second refusal is a new
 * `failedAt` even when its delay matches the first.
 *
 * One timer re-renders at the deadline instead of ticking every second.
 * Nothing on screen needs to change during the wait, which is what keeps the
 * notice from being auto-updating content a screen reader re-announces. A
 * timer held back in a background tab fires on return, which is still correct
 * because the deadline has passed by then.
 */
export function useRetryAt(
  retryAfterSeconds: number | undefined,
  failedAt: number
): { readyAt?: number; isReady: boolean } {
  const readyAt =
    retryAfterSeconds && retryAfterSeconds > 0 ? failedAt + retryAfterSeconds * 1000 : undefined;

  /**
   * Only a trigger to re-render at the deadline. `isReady` itself is read from
   * the clock on every render, so a new refusal is never seen as ready for the
   * one render it would take a stored flag to catch up.
   */
  const [, setDeadlinePassed] = useState(0);

  useEffect(() => {
    if (readyAt === undefined || hasPassed(readyAt)) {
      return;
    }

    /** A little past the deadline, so a timer that fires a hair early still finds it passed. */
    const delayMs = readyAt - Date.now() + 50;

    const timer = setTimeout(() => setDeadlinePassed((count) => count + 1), delayMs);

    return () => clearTimeout(timer);
  }, [readyAt]);

  return { readyAt, isReady: hasPassed(readyAt) };
}

const hasPassed = (readyAt?: number) => readyAt === undefined || Date.now() >= readyAt;

/**
 * Says when a wait ends as a fixed moment rather than a running countdown:
 * a clock time for a wait of a minute or more, and "in under a minute" for a
 * shorter one, where a clock time reads oddly.
 */
export function describeReadyAt(readyAt: number, now = Date.now()): string {
  if (readyAt - now < 60_000) {
    return 'in under a minute';
  }

  return `at ${format(readyAt, 'h:mm a')}`;
}
